<template>
  <div class="layout">
    <aside class="sidebar">
      <div class="brand">
        <div class="brand-mark">{{ brandMark }}</div>
        <div class="brand-copy">
          <div class="brand-title">{{ brandTitle }}</div>
          <div class="brand-subtitle">{{ brandSubtitle }}</div>
        </div>
      </div>

      <nav class="nav">
        <RouterLink v-for="item in navItems" :key="item.to" class="nav-item" :to="item.to">
          <el-icon><component :is="item.icon" /></el-icon>
          <span>{{ item.label }}</span>
        </RouterLink>
      </nav>
    </aside>

    <div class="main">
      <header class="header">
        <div class="header-main">
          <div class="header-title">{{ headerTitle }}</div>
          <div class="header-subtitle">{{ headerSubtitle }}</div>
        </div>

        <div class="header-actions">
          <div v-if="userStore.isEnterprise" class="quick-actions">
            <el-button type="primary" @click="router.push({ path: '/reports/new', query: { mode: 'word' } })">
              <el-icon><UploadFilled /></el-icon>
              上传 Word
            </el-button>
            <el-button @click="router.push('/reports/new')">
              <el-icon><EditPen /></el-icon>
              在线新建
            </el-button>
            <el-button plain @click="router.push('/reports')">
              <el-icon><Document /></el-icon>
              我的周报
            </el-button>
          </div>

          <el-tag size="small" type="info">{{ userStore.roleLabel }}</el-tag>
          <span class="user-name">{{ userStore.displayName }}</span>
          <el-button @click="handleLogout">
            <el-icon><SwitchButton /></el-icon>
            退出登录
          </el-button>
        </div>
      </header>

      <main class="content">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  CircleCheck,
  DataBoard,
  Document,
  EditPen,
  OfficeBuilding,
  SwitchButton,
  UploadFilled
} from '@element-plus/icons-vue';

import { useUserStore } from '../store/user';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

const navItems = computed(() =>
  userStore.isAdmin
    ? [
        { to: '/dashboard', label: '审核概览', icon: DataBoard },
        { to: '/reviews', label: '审核中心', icon: CircleCheck },
        { to: '/enterprise', label: '企业目录', icon: OfficeBuilding }
      ]
    : [
        { to: '/dashboard', label: '工作台', icon: DataBoard },
        { to: '/reports', label: '我的周报', icon: Document },
        { to: '/enterprise', label: '企业信息', icon: OfficeBuilding }
      ]
);

const brandMark = computed(() => (userStore.isAdmin ? '审' : '企'));
const brandTitle = computed(() => (userStore.isAdmin ? '企业周报后台' : '企业周报工作台'));
const brandSubtitle = computed(() => (userStore.isAdmin ? '审核与发布' : '上传与编辑'));

const routeTitleMap = {
  dashboard: '工作台',
  reports: '我的周报',
  'report-create': '新建周报',
  'report-edit': '编辑周报',
  reviews: '审核中心',
  enterprise: '企业信息'
};

const headerTitle = computed(() => routeTitleMap[route.name] || (userStore.isAdmin ? '周报后台' : '企业后台'));
const headerSubtitle = computed(() =>
  userStore.isAdmin ? '集中处理企业提交内容' : '上传、编辑、查看自己的周报'
);

const handleLogout = () => {
  userStore.logout();
  router.push({ name: 'login' });
};
</script>

<style scoped>
.layout {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 240px 1fr;
  background: #f3f4f6;
}

.sidebar {
  border-right: 1px solid #e5e7eb;
  background: #111827;
  color: #fff;
  padding: 20px 16px;
}

.brand {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 24px;
  min-width: 0;
}

.brand-mark {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  flex: 0 0 44px;
}

.brand-copy {
  min-width: 0;
}

.brand-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  word-break: break-word;
}

.brand-subtitle {
  color: #9ca3af;
  font-size: 12px;
  line-height: 1.5;
}

.nav {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  color: #d1d5db;
  min-width: 0;
}

.nav-item.router-link-active {
  background: #1f2937;
  color: #fff;
}

.main {
  min-width: 0;
}

.header {
  min-height: 84px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.header-main {
  min-width: 0;
}

.header-title {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  word-break: break-word;
}

.header-subtitle {
  margin-top: 4px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.quick-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.user-name {
  color: #374151;
  word-break: break-word;
}

.content {
  padding: 24px;
}

@media (max-width: 1080px) {
  .layout {
    grid-template-columns: 88px 1fr;
  }

  .sidebar {
    padding: 16px 12px;
  }

  .brand-copy,
  .nav-item span {
    display: none;
  }

  .nav-item {
    justify-content: center;
    padding: 10px;
  }
}

@media (max-width: 768px) {
  .layout {
    grid-template-columns: 1fr;
  }

  .sidebar {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    padding: 12px;
  }

  .brand-copy,
  .nav-item span {
    display: block;
  }

  .nav {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .nav-item {
    justify-content: flex-start;
    flex: 1 1 140px;
  }

  .header {
    padding: 16px;
  }

  .content {
    padding: 16px;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
}
</style>
